import * as React from 'react';
import cn from 'classnames';
import { motion, useReducedMotion } from 'framer-motion';

import { BaseModalProps } from 'shared/newEntities/components/modals';
import Portal from 'shared/components/Portal';
import { PORTAL_IDS } from 'shared/entities/app';
import BlurrableWrapper from 'shared/components/Blurable';
import ModalEventsHandler from 'shared/components/windows/ModalEventsHandler';
import {
  useModalFocus,
  useTabbing
} from 'shared/components/popups/BasePopup/hooks';

import FadeBackground from '../FadeBackground';

import './BaseModal.modules.scss';

type Props = React.PropsWithChildren<
  BaseModalProps & {
    className?: string;
    style?: React.CSSProperties;
  }
>;

const BaseModal = React.forwardRef(
  (
    {
      opened,
      onClose,
      children,
      style,
      className,
      dismissible = true,
      closeOnEscape = dismissible,
      ariaLabel,
      ariaLabelledBy,
      initialFocusRef,
      fullScreenMobile = false
    }: Props,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const reducedMotion = useReducedMotion();

    useTabbing(contentRef, opened);
    useModalFocus(contentRef, opened, initialFocusRef);

    const setContentRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref]
    );

    return (
      <ModalEventsHandler
        opened={opened}
        onClose={onClose}
        closeOnEscape={closeOnEscape}
        render={({ onClose, zIndex }) => (
          <Portal portalId={PORTAL_IDS.MODALS}>
            <motion.div
              initial={{
                opacity: 0
              }}
              animate={{
                opacity: 1
              }}
              exit={{
                opacity: 0
              }}
              styleName="base-modal-container"
              style={{ zIndex }}
            >
              <FadeBackground styleName="base-modal-fade" />
              <BlurrableWrapper
                styleName={cn(
                  'base-modal',
                  fullScreenMobile && 'base-modal_full-screen-mobile'
                )}
                onBlur={dismissible ? onClose : undefined}
              >
                <motion.div
                  initial={{
                    y: reducedMotion ? 0 : 50,
                    opacity: 0
                  }}
                  animate={{
                    y: 0,
                    opacity: 1
                  }}
                  exit={{
                    y: reducedMotion ? 0 : 50,
                    opacity: 0
                  }}
                  transition={{
                    stiffness: 0,
                    duration: reducedMotion ? 0 : undefined
                  }}
                  styleName={cn(
                    'base-modal__content',
                    fullScreenMobile && 'base-modal__content_full-screen-mobile'
                  )}
                  className={className}
                  style={style}
                  ref={setContentRef}
                  role="dialog"
                  aria-modal="true"
                  aria-label={ariaLabel}
                  aria-labelledby={ariaLabelledBy}
                  tabIndex={-1}
                >
                  {children}
                </motion.div>
              </BlurrableWrapper>
            </motion.div>
          </Portal>
        )}
      />
    );
  }
);

export default BaseModal;
